import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Button, Card, Container, Form, Modal, Spinner } from "react-bootstrap";
import { FileText, Save } from "react-feather";
import { Helmet } from "react-helmet-async";
import CommonTable from "../../components/tables/CommonTable";
import { NavLink } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import api from "../../api";
import { downloadFile } from "../../utils/staticMethods";
import moment from "moment-timezone";
import DateTime from "react-datetime";

const ExportButton = (props) => {
  const { title, onClick } = props;
  const [show, setShow] = useState(false);

  return (
    <>
      <Button
        variant="primary"
        onClick={(e) => {
          setShow(true);
        }}
        className="mx-1 py-0"
      >
        <div className="d-flex justify-content-between align-items-center">
          <Save size={"14"} />
          <div className="d-none d-xl-inline">&nbsp;{title}</div>
        </div>
      </Button>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will create a CSV export file for download. This will be done in
          the background, and may take some time, depending on the size of the
          export. The export will appear in your browser downloads when
          complete.
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={(e) => {
              setShow(false);
              onClick(e);
            }}
            variant={"primary"}
          >
            {title}
          </Button>
          <Button onClick={() => setShow(false)} variant={"secondary"}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

/** DTO classes */
/**
 * @typedef {Object} ScheduleDto
 * @property {string | null} type
 * @property {string | null} time
 * @property {number | null} dayOfWeek
 * @property {string | null} monthlyOption
 * @property {number | null} dayOfMonth
 */

/**
 * @typedef {Object} ScheduledReportCreateDto
 * @property {string | null} reportId
 * @property {ScheduleDto | null} schedule
 */

/**
 * @typedef {Object} ScheduledReportReadDto
 * @property {string | null} scheduledReportId
 * @property {string | null} reportId
 * @property {ScheduleDto | null} schedule
 */

/**
 * @typedef {Object} ScheduledReportUpdateDto
 * @property {string | null} scheduledReportId
 * @property {ScheduleDto | null} schedule
 */

/** UI classes */
/**
 * @typedef SelectOption
 * @type {Object}
 * @property {String} value - the value of the option
 * @property {String} name - the name of the option
 */

/**
 * @typedef SelectOptions
 * @type {SelectOption[]}
 */

/** @type {SelectOptions} */
const scheduleTypeOptions = [
  { value: "daily", name: "Daily" },
  { value: "weekly", name: "Weekly" },
  { value: "monthly", name: "Monthly" },
];

/** @type {SelectOptions} */
const monthlyOptions = [
  { value: "startofmonth", name: "Start of month" },
  { value: "endofmonth", name: "End of month" },
  { value: "specificday", name: "Specific day of month" },
];

// /**
//  * @typedef ScheduleState
//  * @type {Object}
//  * @property {String} scheduleType
//  * @property {String} weekday
//  * @property {String} monthday
//  * @property {String} day
//  * @property {String} time
//  */

/**
 * @typedef {{ type: 'setStateValue'; payload: { name: string, value: object }} | { type: 'init'; payload: ScheduledReportReadDto}} Action
 */

/**
 * @param {ScheduleDto} state
 * @param {Action} action
 */
const reducer = (state, action) => {
  switch (action.type) {
    case "init": {
      var update = Object.assign({}, state);
      update = Object.assign(state, action.payload);
      if (!update.type) update.type = "weekly";
      if (!update.dayOfWeek) update.dayOfWeek = 0;
      if (!update.monthlyOption) update.monthlyOption = "startofmonth";
      if (!update.dayOfMonth) update.dayOfMonth = 1;
      if (!update.time) update.time = "08:00";
      if (update.time.length > 5) update.time = update.time.substring(0, 5);
      return update;
    }

    case "setStateValue": {
      const update = Object.assign({}, state);
      update[action.payload.name] = action.payload.value;
      return update;
    }

    default:
      return state;
  }
};

/**
 * @typedef ScheduleButtonParams
 * @type {object}
 * @property {string} reportId
 * @property {string} reportName
 * @property {string} scheduledReportId
 * @property {React.ref} tableRef
 */

/**
 * @param {ScheduleButtonParams} props
 * @returns {HTMLElement}
 */
const ScheduleButton = ({
  reportId,
  reportName,
  scheduledReportId,
  tableRef,
}) => {
  const [show, setShow] = useState(false);
  const [showRemoveSchedule, setShowRemoveSchedule] = useState(false);
  const [waiting, setWaiting] = useState(false);

  // const title = scheduledReportId ? "Scheduled" : "Schedule";

  /** @type {[ScheduleDto, React.Dispatch<Action>]} */
  const [state, dispatch] = useReducer(reducer, {
    type: "weekly",
    dayOfWeek: 0,
    monthlyOption: "startofmonth",
    dayOfMonth: 1,
    time: "08:00",
  });

  useEffect(() => {
    if (!scheduledReportId || !show) return;
    setWaiting(true);
    const controller = new AbortController();
    api
      .get(`reports/schedule/${scheduledReportId}`, {
        signal: controller.signal,
      })
      .then((response) => {
        /** @type(ScheduledReportReadDto) */
        const schedule = response.data.schedule;
        dispatch({ type: "init", payload: schedule });
      })
      .catch((reason) => {})
      .finally(() => {
        setWaiting(false);
      });

    return () => {
      controller.abort();
    };
  }, [scheduledReportId, show]);

  /** @type {React.ChangeEventHandler<HTMLSelectElement>} */
  const handleTypeChange = (e) => {
    dispatch({
      type: "setStateValue",
      payload: { name: e.target.name, value: e.target.value },
    });
  };

  /** @type {React.ChangeEventHandler<HTMLSelectElement>} */
  const handleDayOfWeekChange = (e) => {
    dispatch({
      type: "setStateValue",
      payload: { name: e.target.name, value: Number(e.target.value) },
    });
  };

  /** @type {React.ChangeEventHandler<HTMLSelectElement>} */
  const handleMonthlyOptionChange = (e) => {
    dispatch({
      type: "setStateValue",
      payload: { name: e.target.name, value: e.target.value },
    });
  };

  /** @type {React.ChangeEventHandler<HTMLSelectElement>} */
  const handleDayOfMonthChange = (e) => {
    dispatch({
      type: "setStateValue",
      payload: { name: e.target.name, value: Number(e.target.value) },
    });
  };

  /** @type {DateTime.EventOrValueHandler<Event>} */
  const handleTimeChange = (e) => {
    if (typeof e === "string") {
      dispatch({
        type: "setStateValue",
        payload: { name: "time", value: e },
      });
    } else if (typeof e === "object" && moment.isMoment(e)) {
      dispatch({
        type: "setStateValue",
        payload: { name: "time", value: moment(e).format("HH:mm") },
      });
    }
  };

  const handleSave = () => {
    if (scheduledReportId) {
      /** @type(ScheduledReportUpdateDto) */
      const update = { scheduledReportId: scheduledReportId, schedule: state };
      if (update.schedule.time.length === 5)
        update.schedule.time = update.schedule.time + ":00";
      setWaiting(true);
      api
        .put("reports/schedule", update)
        .then((response) => {
          tableRef.current.updateTable();
          setShow(false);
          setShowRemoveSchedule(false);
        })
        .finally(() => {
          setWaiting(false);
        });
    } else {
      /** @type(ScheduledReportCreateDto) */
      const create = { reportId: reportId, schedule: state };
      if (create.schedule.time.length === 5)
        create.schedule.time = create.schedule.time + ":00";
      setWaiting(true);
      api
        .post("reports/schedule", create)
        .then((response) => {
          tableRef.current.updateTable();
          setShow(false);
          setShowRemoveSchedule(false);
        })
        .finally(() => {
          setWaiting(false);
        });
    }
  };

  /** @type {React.EventHandler<Event>} */
  const handleCancel = () => {
    setShow(false);
    setShowRemoveSchedule(false);
  };

  /** @type {React.EventHandler<Event>} */
  const handleRemove = () => {
    setWaiting(true);
    api
      .delete(`reports/schedule/${scheduledReportId}`)
      .then((response) => {
        tableRef.current.updateTable();
        setShow(false);
        setShowRemoveSchedule(false);
      })
      .finally(() => {
        setWaiting(false);
      });
  };

  return (
    <>
      <Button
        variant={scheduledReportId ? "warning" : "primary"}
        onClick={(e) => {
          setShow(true);
        }}
        className="mx-1 py-0"
      >
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-none d-xl-inline">
            {scheduledReportId ? "Reschedule" : "Schedule"}
          </div>
        </div>
      </Button>
      <Modal show={show} onHide={() => setShow(false)} backdrop={"static"}>
        <Modal.Header closeButton>
          <Modal.Title>
            Schedule the report &quot;{reportName}&quot;
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card>
            <Card.Body>
              {waiting ? (
                <div className="w-100 justify-content-center d-flex">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div>
                      A CSV file of the report will be emailed to your
                      registered email address, according to the schedule that
                      you create below:
                    </div>
                  </div>
                  <Form.Group
                    className="mb-3"
                    controlId="formGroupThresholdType"
                  >
                    <Form.Label>Schedule Frequency</Form.Label>
                    <Form.Select
                      name="type"
                      value={state.type}
                      onChange={handleTypeChange}
                      onBlur={handleTypeChange}
                    >
                      {scheduleTypeOptions.map((v, i) => {
                        return (
                          <option key={v.value} value={v.value}>
                            {v.name}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>

                  {state.type === "weekly" && (
                    <Form.Group
                      className="mb-3"
                      controlId="formGroupThresholdType"
                    >
                      <Form.Label>Day of Week</Form.Label>
                      <Form.Select
                        name="dayOfWeek"
                        value={state.dayOfWeek}
                        onChange={handleDayOfWeekChange}
                        onBlur={handleDayOfWeekChange}
                      >
                        {moment.weekdays().map((v, i) => {
                          return (
                            <option key={i} value={i}>
                              {v}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  )}

                  {state.type === "monthly" && (
                    <Form.Group
                      className="mb-3"
                      controlId="formGroupThresholdType"
                    >
                      <Form.Label>Monthly Option</Form.Label>
                      <Form.Select
                        name="monthlyOption"
                        value={state.monthlyOption}
                        onChange={handleMonthlyOptionChange}
                        onBlur={handleMonthlyOptionChange}
                      >
                        {monthlyOptions.map((v, i) => {
                          return (
                            <option key={v.value} value={v.value}>
                              {v.name}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </Form.Group>
                  )}

                  {state.type === "monthly" &&
                    state.monthlyOption === "specificday" && (
                      <Form.Group
                        className="mb-3"
                        controlId="formGroupThresholdType"
                      >
                        <Form.Label>Day of month</Form.Label>
                        <Form.Select
                          name="dayOfMonth"
                          value={state.dayOfMonth}
                          onChange={handleDayOfMonthChange}
                          onBlur={handleDayOfMonthChange}
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(
                            (v, i) => {
                              return (
                                <option key={i} value={v}>
                                  {v.toString()}
                                </option>
                              );
                            }
                          )}
                        </Form.Select>
                      </Form.Group>
                    )}

                  <Form.Group
                    className="mb-3"
                    controlId="formGroupThresholdType"
                  >
                    <Form.Label>Time</Form.Label>
                    <DateTime
                      className="no-change-date-picker"
                      inputProps={{ readOnly: true }}
                      value={state.time}
                      onChange={handleTimeChange}
                      timeFormat="HH:mm"
                      dateFormat={false}
                      utc={true}
                    />
                  </Form.Group>
                </>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex">
            <div className="">
              <Button
                onClick={
                  /** @type {React.EventHandler<Event>} */ (e) => {
                    handleSave();
                  }
                }
                variant={"primary"}
                className="me-2"
                disabled={waiting}
              >
                Save
              </Button>
              {scheduledReportId && (
                <Button
                  onClick={
                    /** @type {React.EventHandler<Event>} */ (e) => {
                      setShowRemoveSchedule(true);
                      setShow(false);
                    }
                  }
                  variant={"danger"}
                  className="me-2"
                  disabled={waiting}
                >
                  Remove
                </Button>
              )}
              <Button
                onClick={
                  /** @type {React.EventHandler<Event>} */ (e) => {
                    handleCancel();
                  }
                }
                variant={"secondary"}
                disabled={waiting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
      <Modal
        size="sm"
        show={showRemoveSchedule}
        onHide={() => {
          setShowRemoveSchedule(false);
          setShow(true);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Please Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure that you wish to permanently remove the Schedule for
            Report &quot;{reportName}&quot;?
          </p>
          <p>This action is not reversible.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="danger"
            onClick={() => handleRemove()}
            disabled={waiting}
          >
            Remove
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setShowRemoveSchedule(false);
              setShow(true);
            }}
            disabled={waiting}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

/**
 * @typedef {Object} ColumnConfig
 * @property {string} columnId
 * @property {boolean} isSorted
 * @property {boolean} isSortedDesc
 * @property {boolean} isVisible
 * @property {string | null} filterValue
 */

/**
 * @typedef {Object} Report
 * @property {string} id
 * @property {string} customerId
 * @property {string} type
 * @property {string} name
 * @property {boolean} allUsers
 * @property {number} pageSize
 * @property {string[] | null} permissionIds
 * @property {string} ownerId
 * @property {ColumnConfig[]} columns
 * @property {string | null} scheduledReportId
 */

const Reports = ({ reportType, children, ...rest }) => {
  const [resultCount, setResultCount] = useState(0);
  const [showDelete, setShowDelete] = useState(false);
  const [actionId, setActionId] = useState();
  const tableRef = useRef();
  const { user } = useAuth();

  const handleExport = async (report) => {
    try {
      var sortColumns = report.columns
        .filter((_) => _.isSorted === true)
        .map((_) => {
          return { id: _.columnId, desc: _.isSortedDesc };
        });
      var filterColumns = report.columns
        .filter((_) => _.filterValue !== null)
        .map((_) => {
          return { id: _.columnId, value: _.filterValue };
        });
      var data = {
        pageIndex: 1,
        pageSize: report.pageSize,
        sortBy: sortColumns,
        filters: filterColumns,
      };
      var blob = await api.post("assets/searchexport", data);
      downloadFile(blob);
    } catch (error) {
    } finally {
    }
  };

  const columns = useMemo(
    /**
     * @returns {object[]}
     */
    () => {
      return [
        {
          Header: "Name",
          accessor: "name",
          // type: "link",
          // link: "/rules/edit",
          // stateId: "id",
          Cell: (cell) => (
            <NavLink
              className="m-0 p-0"
              to={{
                pathname: `/${cell.row.original.type}${
                  cell.row.original.type !== "users" ? "/search" : ""
                }`,
                search: `?reportid=${cell.row.original.id}`,
              }}
            >
              {cell.row.original.name}
            </NavLink>
          ),
        },
        {
          Header: "Type",
          accessor: "type",
          // type: "rule",
        },
        {
          Header: "Category",
          Cell: (cell) => (
            <div className="m-0 p-0">
              {cell.row.original.allUsers
                ? `${user.customerName} Report`
                : "Personal Report"}
            </div>
          ),
          disableSortBy: true,
        },
        {
          Header: "Actions",
          Cell: (cell) => (
            <div className="d-flex m-0 p-0">
              <div className="p-0 m-0">
                <ExportButton
                  title={"Export"}
                  onClick={(e) => {
                    handleExport(cell.row.original);
                  }}
                />
              </div>
              <div className="p-0 m-0">
                <NavLink
                  className="m-0 p-0"
                  to={{
                    pathname: `/${cell.row.original.type}${
                      cell.row.original.type !== "users" ? "/search" : ""
                    }`,
                    search: `?reportid=${cell.row.original.id}`,
                  }}
                >
                  <Button className="mx-1 py-0" variant="primary">
                    View
                  </Button>
                </NavLink>
              </div>
              {user?.showMap && cell.row.original.type === "assets" && (
                <NavLink
                  className="m-0 p-0"
                  to={{
                    // pathname: `/report/${cell.row.original.id}/map`,
                    pathname: `/maps?reportid=${cell.row.original.id}`,
                  }}
                >
                  <div className="p-0 m-0">
                    <Button className="mx-1 py-0" variant="primary">
                      View on map
                    </Button>
                  </div>
                </NavLink>
              )}
              <div className="p-0 m-0">
                <ScheduleButton
                  reportId={cell.row.original.id}
                  reportName={cell.row.original.name}
                  scheduledReportId={cell.row.original.scheduledReportId}
                  tableRef={tableRef}
                />
              </div>
              {cell.row.original.ownerId === user.id && (
                <div className="p-0 m-0">
                  <Button
                    className="ms-3 py-0"
                    variant="danger"
                    onClick={(e) => {
                      setActionId(cell.row.original.id);
                      setShowDelete(true);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ),
          disableSortBy: true,
          width: 100,
        },
      ];
    },
    [user]
  );

  const handleDelete = async () => {
    try {
      api.delete("reports/" + actionId);
      tableRef.current.updateTable(1);
    } catch (error) {
      tableRef.current.updateTable(1);
    } finally {
      setActionId(undefined);
      setShowDelete(false);
    }
  };

  return (
    <React.Fragment>
      <Helmet defer={false} title="Reports" />
      <Container fluid className="p-0">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="h3 mb-0">
            <FileText className="me-2" />
            {reportType} Reports{" "}
            {typeof resultCount === "string" && `(${resultCount})`}
          </h1>
        </div>
        <Card className="bg-white p-3">
          <CommonTable
            ref={tableRef}
            setCountParent={setResultCount}
            apiName="reports"
            columns={columns}
          />
        </Card>
      </Container>
      <Modal
        show={showDelete}
        onHide={() => {
          setActionId(undefined);
          setShowDelete(false);
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Please Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure that you wish to permanently delete this Report?</p>
          <p>Deletions are not reversible.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={() => handleDelete()}>
            Delete
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setActionId(undefined);
              setShowDelete(false);
            }}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default Reports;
